import {
  AccountBookOutlined,
  AimOutlined,
  ApartmentOutlined,
  ApiOutlined,
  AppstoreOutlined,
  AuditOutlined,
  BankOutlined,
  BarChartOutlined,
  BookFilled,
  BookOutlined,
  BulbOutlined,
  CalculatorOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  CalendarTwoTone,
  ClockCircleOutlined,
  ClusterOutlined,
  CustomerServiceOutlined,
  DashboardOutlined,
  DatabaseOutlined,
  DeleteOutlined,
  DeploymentUnitOutlined,
  DollarCircleOutlined,
  DollarOutlined,
  FileDoneOutlined,
  FallOutlined,
  FileTextOutlined,
  FunnelPlotOutlined,
  HomeOutlined,
  HourglassOutlined,
  IdcardOutlined,
  InboxOutlined,
  MessageOutlined,
  NotificationOutlined,
  PictureOutlined,
  PlusSquareOutlined,
  PhoneOutlined,
  ProfileOutlined,
  ReadOutlined,
  RiseOutlined,
  SafetyCertificateOutlined,
  ScheduleOutlined,
  ShopOutlined,
  ShoppingCartOutlined,
  ShoppingOutlined,
  SolutionOutlined,
  SoundOutlined,
  SwapOutlined,
  TeamOutlined,
  ThunderboltOutlined,
  TrophyOutlined,
  UserOutlined,
  WalletOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import { Badge, Space } from 'antd'
import type { TFunction } from 'i18next'
import type { ReactNode } from 'react'

/**
 * «محادثات التطبيق» صفحة واحدة بمدخلين: بخدمة العملاء لفريق الكول سنتر، وبالتسويق
 * العقاري بجانب بقية إدارة تطبيق الزبائن — مساران لأن مفاتيح القائمة لا تتكرر.
 */
/** محادثات التطبيق — مدخل واحد بقسم «إدارة تطبيق المشاريع» (مدخل خدمة العملاء أُزيل 2026-10-17) */
export const APP_CHAT_PATH = '/marketing/app-chat'
export const APP_CHAT_PATHS = [APP_CHAT_PATH]

/** عنصر من شجرة القائمة الجانبية: قسم (بأبناء) أو صفحة (مفتاحها مسارها) أو فاصل */
export interface MenuNode {
  key?: string
  icon?: ReactNode
  label?: ReactNode
  type?: 'divider'
  children?: MenuNode[]
}

/** أعداد الشارات على عناصر القائمة (الواردة، المحادثات، المبيعات، الحجوزات، المستحقات) */
export interface MenuBadges {
  inbox?: number
  appChat?: number
  /** مهام الاتصال المفتوحة لموظف خدمة العملاء */
  callTasks?: number
  /** حجوزات بانتظار اتصال التأكيد */
  bookingConfirmations?: number
  sold?: number
  resv?: number
  due?: number
}

/** نص العنصر، وبشارة عدّ إن وُجد — بلا عدٍّ يبقى نصاً خالصاً (يُقرأ في شاشة الصلاحيات) */
function withBadge(text: string, count?: number, overflow?: number): ReactNode {
  if (!count) return text
  return (
    <Space>
      {text}
      <Badge count={count} overflowCount={overflow} />
    </Space>
  )
}

/** عدد كل صفحة بشارة (مفتاحها مسارها) — منه يُجمع عدد القسم الأصل */
export function pageBadgeCounts(badges: MenuBadges = {}): Record<string, number> {
  return {
    '/crm/inbox': badges.inbox ?? 0,
    '/crm/my-call-tasks': badges.callTasks ?? 0,
    '/crm/booking-confirmations': badges.bookingConfirmations ?? 0,
    [APP_CHAT_PATH]: badges.appChat ?? 0,
    '/sales/contracts': badges.sold ?? 0,
    '/sales/reservations': badges.resv ?? 0,
    '/sales/due-installments': badges.due ?? 0,
  }
}

/**
 * شارة القسم الأصل = مجموع شارات صفحاته **الظاهرة للمستخدم** — فيعرف أن بداخله طلباً وهو
 * مطويّ. تُطبَّق على الشجرة بعد فلترة الصلاحيات (لا يُعدّ ما لا يراه).
 */
export function withSectionBadges(tree: MenuNode[], counts: Record<string, number>): MenuNode[] {
  const sum = (nodes: MenuNode[] = []): number =>
    nodes.reduce(
      (acc, n) => acc + (n.key ? (counts[n.key] ?? 0) : 0) + (n.children ? sum(n.children) : 0),
      0,
    )
  return tree.map((s) => {
    if (!s.children || typeof s.label !== 'string') return s
    const total = sum(s.children)
    return total > 0 ? { ...s, label: withBadge(s.label, total, 99) } : s
  })
}

/**
 * شجرة القائمة الجانبية — مصدر واحد للقائمة نفسها ولحارس المسارات ولشاشة الصلاحيات:
 * القسم الأعلى مفتاحه مفتاح صلاحيته (crm، hr…) وكل صفحة مفتاحها مسارها.
 */
export function buildMenuTree(
  t: TFunction,
  opts: { isAdmin: boolean; badges?: MenuBadges },
): MenuNode[] {
  const badges = opts.badges ?? {}
  return [
    { key: '/', icon: <DashboardOutlined />, label: t('nav.dashboard') },
    {
      key: 'accounting',
      icon: <AccountBookOutlined />,
      label: t('nav.accountingSystem'),
      children: [
        {
          key: 'accounts-section',
          icon: <BookOutlined />,
          label: t('nav.accountsSection'),
          children: [
            { key: '/accounts', icon: <BookOutlined />, label: t('nav.chartOfAccounts') },
            // بنود المصروفات والإيرادات — تُضاف حسابات فرعية تحت فرعي 5 و4 من الشجرة
            { key: '/accounts/expenses', icon: <FallOutlined />, label: t('nav.expenseTypes') },
            { key: '/accounts/revenues', icon: <RiseOutlined />, label: t('nav.revenueTypes') },
            { key: '/opening', icon: <PlusSquareOutlined />, label: t('nav.openingEntry') },
            { key: '/journal', icon: <FileTextOutlined />, label: t('nav.journalEntries') },
            { type: 'divider' } as any,
            { key: '/cost-centers', icon: <ApartmentOutlined />, label: t('nav.costCenters') },
            { key: '/currencies', icon: <DollarCircleOutlined />, label: t('nav.currencies') },
            { key: '/fiscal-years', icon: <CalendarOutlined />, label: t('nav.fiscalYears') },
          ],
        },
        {
          key: 'invoices-section',
          icon: <ShoppingCartOutlined />,
          label: t('nav.invoicesSection'),
          children: [
            {
              key: '/invoices/sales',
              icon: <ShoppingCartOutlined />,
              label: t('nav.salesInvoice'),
            },
            {
              key: '/invoices/purchase',
              icon: <ShoppingOutlined />,
              label: t('nav.purchaseInvoice'),
            },
          ],
        },
        {
          key: 'vouchers-section',
          icon: <WalletOutlined />,
          label: t('nav.vouchersSection'),
          children: [
            { key: '/vouchers/receipt', icon: <BankOutlined />, label: t('nav.receiptVoucher') },
            { key: '/vouchers/payment', icon: <WalletOutlined />, label: t('nav.paymentVoucher') },
            {
              key: '/vouchers/campaign-disbursements',
              icon: <WalletOutlined />,
              label: t('nav.campaignDisbursements'),
            },
          ],
        },
        {
          key: 'warehouses-section',
          icon: <InboxOutlined />,
          label: t('nav.warehousesSection'),
          children: [
            { key: '/items', icon: <AppstoreOutlined />, label: t('nav.items') },
            { key: '/item-groups', icon: <ApartmentOutlined />, label: t('nav.itemGroups') },
            { key: '/units', icon: <DeploymentUnitOutlined />, label: t('nav.units') },
            { key: '/warehouses', icon: <DatabaseOutlined />, label: t('nav.warehouses') },
            { type: 'divider' } as any,
            { key: '/stock/new-movement', icon: <SwapOutlined />, label: t('stock.newMovement') },
            { key: '/stock/balances', icon: <DatabaseOutlined />, label: t('nav.stockBalances') },
            { key: '/stock/item-ledger', icon: <FileDoneOutlined />, label: t('nav.itemLedger') },
          ],
        },
        // المشاريع بالحسابات: إدارتها المالية (الأسعار والعمولات والدفعات) شأنٌ محاسبي — المسار نفسه
        {
          key: 'projects-section',
          icon: <BankOutlined />,
          label: t('nav.projectsSection'),
          children: [
            {
              key: '/crm/residential-complexes/finance',
              icon: <WalletOutlined />,
              label: t('nav.complexFinance'),
            },
          ],
        },
        {
          key: 'reports-section',
          icon: <BarChartOutlined />,
          label: t('nav.reportsSection'),
          children: [
            {
              key: '/reports/general-ledger',
              icon: <FileDoneOutlined />,
              label: t('nav.generalLedger'),
            },
            {
              key: '/reports/trial-balance',
              icon: <BarChartOutlined />,
              label: t('nav.trialBalance'),
            },
            {
              key: '/reports/balance-sheet',
              icon: <BarChartOutlined />,
              label: t('nav.balanceSheet'),
            },
            {
              key: '/reports/income-statement',
              icon: <BarChartOutlined />,
              label: t('nav.incomeStatement'),
            },
            {
              key: '/reports/cost-centers',
              icon: <ApartmentOutlined />,
              label: t('nav.costCentersReport'),
            },
            { type: 'divider' } as any,
            { key: '/reports/sales', icon: <ShoppingCartOutlined />, label: t('nav.salesReport') },
            {
              key: '/reports/purchases',
              icon: <ShoppingOutlined />,
              label: t('nav.purchasesReport'),
            },
            {
              key: '/reports/receipt-vouchers',
              icon: <BankOutlined />,
              label: t('nav.receiptVouchersReport'),
            },
            {
              key: '/reports/payment-vouchers',
              icon: <WalletOutlined />,
              label: t('nav.paymentVouchersReport'),
            },
          ],
        },
      ],
    },
    {
      key: 'hr',
      icon: <TeamOutlined />,
      label: t('nav.hr'),
      children: [
        { key: '/hr/dashboard', icon: <DashboardOutlined />, label: 'لوحة الموارد البشرية' },
        { key: '/employees', icon: <IdcardOutlined />, label: t('nav.employees') },
        { key: '/departments', icon: <ApartmentOutlined />, label: t('nav.departments') },
        { key: '/job-titles', icon: <SolutionOutlined />, label: t('nav.jobTitles') },
        { type: 'divider' } as any,
        { key: '/shifts', icon: <ClockCircleOutlined />, label: t('nav.shifts') },
        { key: '/shift-assignments', icon: <ScheduleOutlined />, label: 'إسناد الشفتات' },
        { key: '/roster', icon: <CalendarTwoTone />, label: 'إسناد تحديد دوام' },
        { key: '/day-exceptions', icon: <ThunderboltOutlined />, label: 'الإجازات الزمنية' },
        { key: '/hr-alerts', icon: <WarningOutlined />, label: 'تنبيهات الانتهاء' },
        { key: '/separations', icon: <SolutionOutlined />, label: 'إنهاء الخدمة والعهدة' },
        { key: '/hr-letters', icon: <FileTextOutlined />, label: 'الكتب الرسمية' },
        { key: '/org-chart', icon: <ApartmentOutlined />, label: 'الهيكل التنظيمي' },
        { key: '/flex-report', icon: <ClockCircleOutlined />, label: 'الدوام المرن' },
        { key: '/hr/open-ended-report', icon: <HourglassOutlined />, label: 'الشفت مفتوح النهاية' },
        {
          key: '/hr/penalty-waivers',
          icon: <SafetyCertificateOutlined />,
          label: 'إلغاء الخصومات',
        },
        { key: '/hr/instant-punch', icon: <ThunderboltOutlined />, label: 'البصمة الفورية' },
        { key: '/hr/devices', icon: <ApiOutlined />, label: 'أجهزة البصمة' },
        { key: '/hr/promotions', icon: <RiseOutlined />, label: 'الترقيات' },
        { key: '/hr/tasks', icon: <ProfileOutlined />, label: 'مهام الموظفين' },
        { key: '/hr/recruitment', icon: <SolutionOutlined />, label: 'أرشيف السِّيَر الذاتية' },
        { key: '/hr/announcements', icon: <NotificationOutlined />, label: 'إشعارات الموظفين' },
        { key: '/public-holidays', icon: <CalendarTwoTone />, label: t('nav.publicHolidays') },
        { key: '/attendance', icon: <ClockCircleOutlined />, label: t('nav.attendance') },
        { type: 'divider' } as any,
        { key: '/leave-types', icon: <BulbOutlined />, label: t('nav.leaveTypes') },
        { key: '/leaves', icon: <AuditOutlined />, label: t('nav.leaveRequests') },
        { key: '/leave-balances', icon: <BookFilled />, label: t('nav.leaveBalances') },
        { type: 'divider' } as any,
        { key: '/salary-components', icon: <DollarOutlined />, label: t('nav.salaryComponents') },
        { key: '/payroll/runs', icon: <DollarCircleOutlined />, label: t('nav.payrollRuns') },
        { key: '/hr/tax-rules', icon: <CalculatorOutlined />, label: t('nav.taxRules') },
        {
          key: '/hr/file-completeness',
          icon: <FileDoneOutlined />,
          label: t('nav.fileCompleteness'),
        },
        { key: '/hr/media-payouts', icon: <SoundOutlined />, label: t('nav.mediaPayouts') },
        { type: 'divider' } as any,
        { key: '/loans', icon: <DollarCircleOutlined />, label: t('nav.loans') },
        { key: '/violations', icon: <WarningOutlined />, label: t('nav.violations') },
        { key: '/performance', icon: <TrophyOutlined />, label: t('nav.performance') },
        { key: '/training', icon: <ReadOutlined />, label: t('nav.training') },
      ],
    },
    {
      key: 'crm',
      icon: <CustomerServiceOutlined />,
      label: t('nav.crm'),
      children: [
        // نبض خدمة العملاء: مسار الأسماء وما يحتاج إجراءً وحمل الفريق والأرقام المهمة من كل شاشات القسم
        { key: '/crm/cs-followup', icon: <DashboardOutlined />, label: t('nav.csFollowup') },
        {
          key: '/crm/inbox',
          icon: <InboxOutlined />,
          label: withBadge(t('nav.inbox'), badges.inbox, 99),
        },
        { key: '/crm/books/new', icon: <PlusSquareOutlined />, label: t('nav.newBook') },
        // حجوزات «إضافة معلومات الزبائن» بانتظار اتصال التأكيد قبل مدير الفرع — شارتها عددها
        {
          key: '/crm/booking-confirmations',
          icon: <CheckCircleOutlined />,
          label: withBadge(t('nav.bookingConfirmations'), badges.bookingConfirmations, 99),
        },
        { key: '/crm/lookups', icon: <ProfileOutlined />, label: t('nav.crmLookups') },
        {
          key: '/crm/callcenter-report',
          icon: <BookOutlined />,
          label: t('nav.callCenterReport'),
        },
        { key: '/crm/media-leads', icon: <SoundOutlined />, label: t('nav.mediaLeads') },
        // مهام موظف خدمة العملاء — تفتحها إشعارات «أسماء جديدة للاتصال»، وشارتها عدد المفتوحة
        {
          key: '/crm/my-call-tasks',
          icon: <PhoneOutlined />,
          label: withBadge(t('nav.myCallTasks'), badges.callTasks, 99),
        },
        // كل الموزَّع لدى الكول سنتر: نقلٌ وسحبٌ وتعديلٌ وحذف — بصلاحية صفحة لا بدور
        { key: '/crm/team-call-tasks', icon: <TeamOutlined />, label: t('nav.teamCallTasks') },
        {
          key: '/crm/media-leads/deleted',
          icon: <DeleteOutlined />,
          label: t('nav.mediaLeadsTrash'),
        },
        {
          key: '/crm/reports/unified-numbers',
          icon: <DatabaseOutlined />,
          label: t('nav.unifiedNumbersReport'),
        },
        {
          key: '/crm/reports/media-performance',
          icon: <TrophyOutlined />,
          label: t('nav.mediaPerformanceReport'),
        },
        {
          key: '/crm/reports/sales-actions',
          icon: <SolutionOutlined />,
          label: t('nav.salesActionsReport'),
        },
        {
          key: '/crm/reports/branches',
          icon: <ApartmentOutlined />,
          label: t('nav.branchesReport'),
        },
      ],
    },
    {
      key: 'sales',
      icon: <DollarOutlined />,
      label: t('nav.salesModule'),
      children: [
        {
          key: '/sales/contracts',
          icon: <FileDoneOutlined />,
          label: withBadge(t('nav.saleContracts'), badges.sold),
        },
        {
          key: '/sales/reservations',
          icon: <ThunderboltOutlined />,
          label: withBadge(t('nav.preliminaryReservations'), badges.resv),
        },
        {
          key: '/sales/due-installments',
          icon: <CalendarOutlined />,
          label: withBadge(t('nav.dueInstallments'), badges.due),
        },
        { key: '/sales/commissions', icon: <DollarOutlined />, label: t('nav.salesCommissions') },
      ],
    },
    {
      key: 'realestate-marketing',
      icon: <ShopOutlined />,
      label: t('nav.realEstateMarketing'),
      children: [
        // أعلى القسم: ما يكتبه الزبائن الآن يسبق إدارة المحتوى
        {
          key: APP_CHAT_PATH,
          icon: <MessageOutlined />,
          label: withBadge(t('nav.appChat'), badges.appChat, 99),
        },
        { key: '/marketing/catalog', icon: <AppstoreOutlined />, label: t('nav.marketedProjects') },
        { key: '/marketing/home-sections', icon: <PictureOutlined />, label: t('nav.banners') },
        { key: '/marketing/developers', icon: <BankOutlined />, label: t('nav.developers') },
        // «الشريك العقاري»: طلبات المكاتب والشركات والمسوّقين — مراجعة الوثائق واعتمادها
        {
          key: '/marketing/partners',
          icon: <IdcardOutlined />,
          label: t('nav.realEstatePartners'),
        },
        // «قبول عقارات الشركاء»: عقارات الشركاء المعتمدين قبل نشرها في التطبيق
        {
          key: '/marketing/partner-listings',
          icon: <HomeOutlined />,
          label: t('nav.partnerListings'),
        },
        { key: '/marketing/app-accounts', icon: <TeamOutlined />, label: t('nav.appAccounts') },
        { key: '/marketing/funnel', icon: <FunnelPlotOutlined />, label: t('nav.funnel') },
        { key: '/marketing/trust', icon: <SafetyCertificateOutlined />, label: t('nav.trust') },
        {
          key: '/marketing/app-campaigns',
          icon: <NotificationOutlined />,
          label: t('nav.appCampaigns'),
        },
      ],
    },
    {
      key: 'marketing',
      icon: <BulbOutlined />,
      label: t('nav.marketingModule'),
      children: [
        { key: '/marketing/campaigns', icon: <ThunderboltOutlined />, label: t('nav.campaigns') },
        { key: '/marketing/content', icon: <CalendarOutlined />, label: t('nav.contentCalendar') },
        { key: '/marketing/production', icon: <AppstoreOutlined />, label: t('nav.production') },
        { key: '/marketing/suppliers', icon: <TeamOutlined />, label: t('nav.marketingSuppliers') },
        { key: '/marketing/budget', icon: <WalletOutlined />, label: t('nav.marketingBudget') },
        {
          key: '/marketing/effectiveness',
          icon: <BarChartOutlined />,
          label: t('nav.campaignEffectiveness'),
        },
      ],
    },
    {
      key: 'realestate',
      icon: <BankOutlined />,
      label: t('nav.realEstate'),
      children: [
        {
          key: '/crm/residential-complexes',
          icon: <BankOutlined />,
          label: t('nav.residentialComplexes'),
        },
        { key: '/crm/unit-status', icon: <ThunderboltOutlined />, label: t('nav.unitStatus') },
        {
          key: '/crm/apartment-features',
          icon: <AppstoreOutlined />,
          label: t('nav.apartmentFeatures'),
        },
        // تاركت المشاريع وقنوات الواردة وخريطة الفريق — إدارة المجمعات نفسها
        { key: '/sales/targets', icon: <AimOutlined />, label: t('nav.salesTargets') },
        { key: '/crm/inbox-channels', icon: <ApiOutlined />, label: t('nav.inboxChannels') },
        // ربط الموظفين بالمجمعات والمدراء ومشاريع الدردشة — شاشة واحدة، تُمنح كأي صفحة
        { key: '/sales/team-map', icon: <ClusterOutlined />, label: t('nav.teamMap') },
      ],
    },
    {
      key: 'users',
      icon: <TeamOutlined />,
      label: t('nav.usersManagement'),
      children: [{ key: '/users', icon: <UserOutlined />, label: t('nav.users') }],
    },
  ]
}
